import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { appRoutes, type AppRouteConfig } from '@/router/routes.config'

/** Navigation item for the sidebar menu */
export interface NavItem {
  title: string
  url: string
  icon?: AppRouteConfig['nav']['icon']
  isActive?: boolean
}

/**
 * Composable for navigation items based on user authentication and roles
 */
export function useNavigation() {
  const authStore = useAuthStore()

  /**
   * Check if a route is accessible to the current user
   */
  function canAccessRoute(route: AppRouteConfig): boolean {
    const { access } = route
    const isAuthenticated = authStore.isAuthenticated
    const isTeacher = authStore.isTeacher

    // Public routes are always accessible
    if (access.public) {
      return true
    }

    // Guest-only routes are hidden for authenticated users
    if (access.guestOnly && isAuthenticated) {
      return false
    }

    // Auth-required routes are hidden for guests
    if (access.requiresAuth && !isAuthenticated) {
      return false
    }

    // Teacher-only routes require teacher role
    if (access.requiresTeacher && !isTeacher) {
      return false
    }

    return true
  }

  /**
   * Navigation items filtered by user access and visibility
   */
  const navItems = computed<NavItem[]>(() => {
    return appRoutes
      .filter((route) => route.nav.showInNav && canAccessRoute(route))
      .sort((a, b) => (a.nav.order ?? 99) - (b.nav.order ?? 99))
      .map((route) => ({
        title: route.nav.title,
        url: route.path,
        icon: route.nav.icon,
      }))
  })

  return {
    navItems,
    canAccessRoute,
  }
}
